import condriLogo from "@/assets/condri-without-background.png"
import {
  AlertTriangle,
  Eye,
  EyeOff,
  CheckCircle,
  Loader2,
} from "lucide-react"
import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { validatePassword } from "@/lib/validation"
import { Auth } from "@/heyapi/sdk.gen"
import { AppStoreButtons } from "@/components/ui/AppStores/AppStoreButtons"
import { safeError } from "@/utils/safeError"

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")

  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing reset token. Please request a new password reset.")
    }
  }, [token])

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    if (error) {
      setError(null)
    }
  }

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(e.target.value)
    if (error) {
      setError(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!token) {
      setError("Invalid or missing reset token. Please request a new password reset.")
      return
    }

    // Validate password
    const passwordValidation = validatePassword(password)
    if (!passwordValidation.isValid) {
      setError(passwordValidation.error!)
      return
    }

    // Check passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match")
      return
    }

    setLoading(true)
    try {
      await Auth.authResetPassword({
        body: {
          token,
          new_password: password,
        },
      })
      setSuccess(true)
      toast({
        title: "Password reset successful!",
        description: "Your password has been updated. Download the app to sign in.",
      })
    } catch (err: any) {
      console.error("Password reset failed:", safeError(err))
      const errorMessage =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to reset password. The token may be invalid or expired. Please request a new password reset."
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen max-w-md mx-auto bg-card flex flex-col">
        <div className="flex-1 flex flex-col justify-center px-6 pt-8">
          <div className="text-center space-y-6">
            <div className="mx-auto w-20 h-20 bg-primary-faded rounded-full flex items-center justify-center p-2">
              <img
                src={condriLogo}
                alt="Condri Logo"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="space-y-4">
              <div className="mx-auto w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-success" />
              </div>

              <div className="space-y-2">
                <h1 className="text-3xl font-bold text-foreground">
                  Password Reset Successful
                </h1>
                <p className="text-muted-foreground text-lg">
                  Your password has been updated successfully.
                </p>
                <p className="text-sm text-muted-foreground">
                  Download the Condri app to sign in with your new password.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 space-y-4">
          <AppStoreButtons />
          <p className="text-xs text-center text-muted-foreground">
            Your password has been reset. Use the app to sign in.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen max-w-md mx-auto bg-card flex flex-col">
      <div className="flex-1 flex flex-col justify-center px-6 pt-8">
        <div className="text-center space-y-6">
          <div className="mx-auto w-20 h-20 bg-primary-faded rounded-full flex items-center justify-center p-2">
            <img
              src={condriLogo}
              alt="Condri Logo"
              className="w-full h-full object-contain"
            />
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl font-bold text-foreground">
              Set New Password
            </h1>
            <p className="text-muted-foreground text-lg">
              Enter your new password below
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col px-6 pb-6 space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your new password"
                value={password}
                onChange={handlePasswordChange}
                required
                disabled={loading || !token}
                className="pr-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading || !token}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Password must be at least 8 characters long
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your new password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                required
                disabled={loading || !token}
                className="pr-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading || !token}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-hero hover:bg-gradient-hero/90 text-white font-medium py-3 rounded-lg transition-all duration-300 shadow-soft hover:shadow-glow"
            disabled={loading || !token || !password.trim() || !confirmPassword.trim()}
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resetting password...
              </>
            ) : (
              "Reset Password"
            )}
          </Button>
        </form>

        <div className="text-center space-y-4">
          <Button variant="ghost" asChild className="w-full">
            <a href="/auth/password-recovery">Need a new reset link?</a>
          </Button>
          <div className="pt-4">
            <p className="text-sm text-muted-foreground mb-4">
              Download the Condri app to sign in:
            </p>
            <AppStoreButtons />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage
